/**
 * The trust-score display band, in one place.
 *
 * ## One vocabulary
 *
 * There is a single band set across the whole product: the published method's, at 85 / 70 / 50 / 30.
 * It is defined in `services/trustBands.js` and this module is a thin alias over it, kept so the
 * surfaces that already import `trustBand` keep working.
 *
 * This file used to hold a second band set at 90 / 75 spelling the thirds `strong` / `medium` /
 * `at-risk`. That meant one agent could read Adequate on its scorecard, 78 on a chip and Medium in
 * a filter, all from the same number. A score is a claim about somebody's system; it cannot mean
 * three things depending on which screen it is shown on.
 *
 * ## What is NOT a band
 *
 * `Agent.trustBand` on the backend is the AUTONOMY TIER — it defaults to `SUPERVISED` and is
 * written with rungs and `PROBATION`. Despite the name it has nothing to do with this, and the
 * Identity surfaces that render it must not be moved onto these thresholds.
 */

import { PUBLISHED_BANDS, UNRATED, publishedBand, displayScore } from '../services/trustBands';

/** Lower bound of the top band. */
export const STRONG_MIN = PUBLISHED_BANDS[0].min;

/** Lower bound of the second band. */
export const ADEQUATE_MIN = PUBLISHED_BANDS[1].min;

export const BAND_STRONG   = 'strong';
export const BAND_ADEQUATE = 'adequate';
export const BAND_LIMITED  = 'limited';
export const BAND_WEAK     = 'weak';
export const BAND_CRITICAL = 'critical';
export const BAND_UNRATED  = UNRATED;

/** The bands that mean an agent needs attention — anything below Adequate, but rated. */
export const AT_RISK_BANDS = Object.freeze([BAND_LIMITED, BAND_WEAK, BAND_CRITICAL]);

/**
 * Band for a score, with Unrated preserved.
 *
 * `null`, `undefined` and anything non-numeric are Unrated — no evidence, no band. Deliberately not
 * the same as scoring zero: an agent that has never run has not failed.
 *
 * @param {number|string|null|undefined} score 0–100, or nullish for Unrated
 * @returns {'strong'|'adequate'|'limited'|'weak'|'critical'|'unrated'}
 */
export function trustBand(score) {
  return publishedBand(score);
}

/** True when there is a real score to show. */
export function isRated(score) {
  return trustBand(score) !== BAND_UNRATED;
}

/** True when a score is rated and below Adequate. */
export function isAtRisk(score) {
  return AT_RISK_BANDS.includes(trustBand(score));
}

/** The whole number a surface shows for a score — rounded down. */
export { displayScore };
